/* eslint-disable no-shadow */
/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/naming-convention */
import { MapBrowserEvent, Overlay } from 'ol';
import * as control from 'ol/control';
import Control from 'ol/control/Control';
import { Coordinate } from 'ol/coordinate';
import { EventsKey } from 'ol/events';
import { createEmpty, extend } from 'ol/extent';
import Feature, { FeatureLike } from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import WMTSCapabilities from 'ol/format/WMTSCapabilities';
import Geometry, { Type as GeometryType } from 'ol/geom/Geometry';
import LineString from 'ol/geom/LineString';
import Point from 'ol/geom/Point';
import Polygon from 'ol/geom/Polygon';
import Draw from 'ol/interaction/Draw';
import Layer from 'ol/layer/Layer';
import Tile from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import Map from 'ol/Map';
import { unByKey } from 'ol/Observable';
import * as proj from 'ol/proj';
import * as olProjProj4 from 'ol/proj/proj4';
import { Cluster } from 'ol/source';
import BingMaps from 'ol/source/BingMaps';
import TileWMS from 'ol/source/TileWMS';
import VectorSource from 'ol/source/Vector';
import WMTS, { optionsFromCapabilities } from 'ol/source/WMTS';
import olSourceXYZ from 'ol/source/XYZ';
import { RegularShape, Text } from 'ol/style';
import { default as Circle, default as CircleStyle } from 'ol/style/Circle';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import View from 'ol/View';
import { MapboxVectorLayer } from 'ol-mapbox-style';
import proj4 from 'proj4';
import React from 'react';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { translateKeyLang } from '../../../../common/src/translate/translateHelper';
import { appContext } from '../appContext';
import { ComponentSetStatePromised } from './ComponentSetStatePromised';
import { formatArea, formatLength } from './mapHelpers';

type ReactTooltipPosition = {
    left: number;
    top: number;
};

type ExtendedFeature = FeatureLike & {
    values_?: { features?: ExtendedFeature[]; marker?: IMarker; conditionLabel?: { backgroundColor: string; text: string } };
};

const zoomLevelToStopClustering = 15;
export interface IMarker {
    key: string;
    position: {
        lat: number;
        lng: number;
    };
    title: string;
    onClick?: ((marker: IMarker, event: any) => void) | undefined;
    shapeGeoJson?: object | undefined;
    color?: string | undefined;
    isTerrain?: boolean | undefined;
    conditionLabel?: { text: string | null } | undefined;
    condition?: string | undefined;
}

export type Bounds = { xMin: number; yMin: number; xMax: number; yMax: number };
interface IGenericMapProps {
    center: { lat: number; lng: number };
    zoom: number;
    markers?: IMarker[] | undefined;
    inLine?: boolean | undefined;
    isPdokMapLayerAvailable?: boolean | undefined;
    isDroneNoFlyMapNlAvailable?: boolean | undefined;
    shouldClusterMarkers?: boolean | undefined;
    mapLoaded?: () => void;
    mapClicked?: (latitude: number, longitude: number) => void;
    children?: React.ReactNode;
    onShapeGeoJsonChange?: (shapeGeoJson: Record<string, unknown> | undefined) => void;
    shapeGeoJson?: Record<string, unknown> | undefined;
    isDrawable?: boolean | undefined;
    isShowObjects?: boolean | undefined;
    onBoundsChanged?: (bounds: Bounds) => void;
    onZoomChanged?: (zoom: number) => void;
    onShowObjects?: () => void;
    markersToFit?: IMarker[] | undefined;
}

interface IGenericMapState {
    isMeasurementActive: boolean;
    pdokLayersVisible: boolean;
    droneNoFlyLayersVisible: boolean;
    extraLayersVisibility: { [key: string]: boolean };
    tooltipPosition: ReactTooltipPosition;
    tooltipText: string;
    isToolTipVisible: boolean;
    drawType: GeometryType;
    zoom: number;
}

function getBounds(map: Map) {
    const extent = map.getView().calculateExtent(map.getSize());

    const gpsExtent = proj.transformExtent(extent, 'EPSG:3857', 'EPSG:4326');

    return { xMin: gpsExtent[1]!, yMin: gpsExtent[0]!, xMax: gpsExtent[3]!, yMax: gpsExtent[2]! };
}

function createMapControl(text: string, className: string, onClick: () => void) {
    const button = document.createElement('button');
    button.innerHTML = text;
    const element = document.createElement('div');
    element.className = `ol-control ${className}`;
    element.appendChild(button);
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const control = new Control({ element });
    button.addEventListener('click', onClick, false);
    return control;
}

export class GenericMap extends ComponentSetStatePromised<IGenericMapProps, IGenericMapState> {
    private map!: Map;
    private mapDiv: any;
    private vectorLayer?: any;
    private generalMapLayer?: any;
    private generalSatelliteLayer?: any;
    private pdokMapLayer?: any;
    private pdokSatelliteLayer?: any;
    private pdokInfraRedLayer?: any;
    private wmsDroneNoFlyLayer?: any;
    private measurementDraw?: Draw;
    private measurementSource?: VectorSource<any>;
    private measurementElements: HTMLElement[] = [];
    private drawSource?: VectorSource<any>;
    private draw?: Draw;
    private clusterVectorLayer?: VectorLayer<any>;

    constructor(props: IGenericMapProps) {
        super(props);
        this.mapDiv = React.createRef();
        this.state = {
            isMeasurementActive: false,
            extraLayersVisibility: {},
            droneNoFlyLayersVisible: false,
            pdokLayersVisible: this.props.isPdokMapLayerAvailable ?? false,
            tooltipPosition: { left: 0, top: 0 },
            tooltipText: '',
            isToolTipVisible: false,
            drawType: 'LineString',
            zoom: this.props.zoom,
        };
    }

    public override async componentDidMount() {
        // <script src="https://epsg.io/28992.js"></script>
        proj4.defs(
            'EPSG:28992',
            '+proj=sterea +lat_0=52.15616055555555 +lon_0=5.38763888888889 +k=0.9999079 +x_0=155000 +y_0=463000 +ellps=bessel +towgs84=565.417,50.3319,465.552,-0.398957,0.343988,-1.8774,4.0725 +units=m +no_defs',
        );

        olProjProj4.register(proj4);
        const layers: Layer<any>[] = [];

        this.generalMapLayer = new MapboxVectorLayer({
            styleUrl: 'mapbox://styles/zxy-cloud/cl95kmba300dr15qum5dtcclv',
            accessToken: (window as any).zxySettings.mapboxApiKey,
            preload: 2,
            visible: !this.state.pdokLayersVisible,
        });
        layers.push(this.generalMapLayer);

        // Bing Satellite
        this.generalSatelliteLayer = new Tile({
            visible: false,
            preload: 2,
            source: new BingMaps({
                key: (window as any).zxySettings.bingApiKey,
                imagerySet: 'Aerial',
                placeholderTiles: undefined as unknown as boolean, // TODO: https://github.com/openlayers/openlayers/pull/15027
            }),
        });
        layers.push(this.generalSatelliteLayer);

        // Pdok Map
        this.pdokMapLayer = new Tile({
            preload: Infinity,
            visible: this.state.pdokLayersVisible,
            source: new olSourceXYZ({
                url: 'https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0/standaard/EPSG:3857/{z}/{x}/{y}.png',
            }),
        });
        layers.push(this.pdokMapLayer);

        // Pdok Satelite
        this.pdokSatelliteLayer = new Tile({
            visible: false,
            preload: 2,
            source: await this.getPdokTileSource('2023_orthoHR', 'rgb'),
            // The default is 'Actueel_ortho25'
            // source: await this.getPdokTileSource('Actueel_ortho25', 'rgb')
        });
        layers.push(this.pdokSatelliteLayer);

        // Pdok Infra red
        this.pdokInfraRedLayer = new Tile({
            visible: false,
            preload: 2,
            source: await this.getPdokTileSource('Actueel_ortho25IR', 'infraRed'),
        });
        layers.push(this.pdokInfraRedLayer);

        // Kadaster layer
        // const wmsSource = new TileWMS({
        //     url: 'https://geodata.nationaalgeoregister.nl/kadastralekaart/wms/v4_0?service=WMS&version=1.3.0&request=GetCapabilities',
        //     params: {
        //         'LAYERS': ['Bebouwing', 'Nummeraanduidingreeks'],
        //         'SRS': 'EPSG:3857',
        //     }
        // });
        // const wmsLayer = new Tile({
        //     source: wmsSource,
        // });
        // layers.push(wmsLayer);

        const wmsDroneNoFlySource = new TileWMS({
            url: 'https://geodata.nationaalgeoregister.nl/dronenoflyzones/wms?request=GetCapabilities&service=wms',
            params: {
                LAYERS: 'dronenoflyzones',
                SRS: 'EPSG:3857',
                crossOrigin: 'anonymous',
            },
        });

        this.wmsDroneNoFlyLayer = new Tile({
            visible: false,
            preload: 2,
            source: wmsDroneNoFlySource,
        });
        layers.push(this.wmsDroneNoFlyLayer);

        const measureLengthControl = createMapControl('<li class="fa fa-arrows-h"></li>', 'map-measure-length-button', () => {
            this.startMeasurement('length');
        });
        const measureAreaControl = createMapControl('<li class="fa fa-square"></li>', 'map-measure-area-button', () => {
            this.startMeasurement('area');
        });
        const removeMeasurementsControl = createMapControl('<li class="fa fa-remove"></li>', 'map-remove-measurements-button', () => {
            this.removeMeasurements();
        });
        const pointerControl = createMapControl('<li class="fa fa-map-marker"></li>', 'map-pointer-button', () => {
            this.stopMeasurements();
        });

        // Satellite button
        const satelliteControl = createMapControl('S', 'map-satellite-button', () => {
            if (this.state.pdokLayersVisible) {
                this.pdokSatelliteLayer.setVisible(true);
            } else {
                this.generalSatelliteLayer.setVisible(true);
            }
            this.generalMapLayer.setVisible(false);
            this.pdokMapLayer.setVisible(false);
            this.pdokInfraRedLayer.setVisible(false);
        });

        // Map button
        const mapButton = document.createElement('button');
        mapButton.innerHTML = 'M';
        const mapElement = document.createElement('div');
        mapElement.className = 'map-map-button ol-control';
        mapElement.appendChild(mapButton);
        const mapControl = new Control({ element: mapElement });
        mapButton.addEventListener(
            'click',
            () => {
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                this.state.pdokLayersVisible ? this.pdokMapLayer.setVisible(true) : this.generalMapLayer.setVisible(true);
                this.pdokSatelliteLayer.setVisible(false);
                this.generalSatelliteLayer.setVisible(false);
                this.pdokInfraRedLayer.setVisible(false);
            },
            false,
        );

        this.drawSource = new VectorSource({ wrapX: false });
        const vector = new VectorLayer({
            source: this.drawSource,
        });

        if (this.props.shapeGeoJson) {
            const features = new GeoJSON().readFeatures(this.props.shapeGeoJson);
            this.drawSource.addFeatures(features);
        }

        // Pdok Ifra red button
        const pdokInfraRedButton = document.createElement('button');
        pdokInfraRedButton.innerHTML = 'I';
        const pdokInfraRedElement = document.createElement('div');
        pdokInfraRedElement.className = this.state.pdokLayersVisible ? 'map-pdok-infra-red-button ol-control' : 'hidden';
        pdokInfraRedElement.appendChild(pdokInfraRedButton);
        const pdokInfraRedControl = new Control({ element: pdokInfraRedElement });
        pdokInfraRedButton.addEventListener(
            'click',
            () => {
                this.pdokInfraRedLayer.setVisible(true);
                this.generalMapLayer.setVisible(false);
                this.pdokMapLayer.setVisible(false);
                this.pdokSatelliteLayer.setVisible(false);
                this.generalSatelliteLayer.setVisible(false);
            },
            false,
        );

        this.map = new Map({
            controls: control.defaults().extend([
                satelliteControl,
                mapControl,
                pdokInfraRedControl,
                // pdokTopoControl,
                measureLengthControl,
                measureAreaControl,
                pointerControl,
                removeMeasurementsControl,
            ]),
            layers: [...layers, vector],
            target: 'map',
            view: new View({
                center: [0, 0],
                zoom: 12,
            }),
        });

        this.map.on('click', (event: any) => {
            if (this.props.mapClicked) {
                const WG84Coordonates = proj.transform(event.coordinate, 'EPSG:3857', 'EPSG:4326');
                this.props.mapClicked(WG84Coordonates[0]!, WG84Coordonates[1]!);
            }

            if (!this.state.isMeasurementActive) {
                this.map.forEachFeatureAtPixel(event.pixel, (f) => {
                    const feature: ExtendedFeature = f as ExtendedFeature;
                    if (this.props.shouldClusterMarkers && this.clusterVectorLayer?.getVisible()) {
                        if (feature.values_?.features?.length === 1) {
                            feature.values_?.features[0]?.values_?.marker?.onClick?.(feature.values_.features[0].values_.marker, event);
                        } else {
                            if (feature.values_) {
                                const clusterExtend = createEmpty();

                                for (const featureInCluster of feature.values_?.features ?? []) {
                                    const featureGeometry = featureInCluster.getGeometry();
                                    if (featureGeometry) {
                                        extend(clusterExtend, featureGeometry.getExtent());
                                    }
                                }

                                this.map
                                    .getView()
                                    .fit([clusterExtend[0]! - 100, clusterExtend[1]! - 100, clusterExtend[2]! + 100, clusterExtend[3]! + 100]);
                            }
                        }
                    } else {
                        feature.values_?.marker?.onClick?.(feature.values_.marker, event);
                    }
                });
            }
        });

        const displayFeatureInfo = (evt: MapBrowserEvent<UIEvent>) => {
            if (evt.dragging) {
                this.setState({ isToolTipVisible: false });
                return;
            }

            const pixel = this.map.getEventPixel(evt.originalEvent);
            const feature = this.map.forEachFeatureAtPixel(pixel, (featureAtPixel) => featureAtPixel) as ExtendedFeature;

            let tooltipText;

            if (feature?.values_?.marker) {
                tooltipText = feature.get('name');
            } else if (feature?.values_?.features?.[0]?.values_?.marker) {
                const subFeatures = feature?.values_?.features;
                if (subFeatures.length === 1) {
                    tooltipText = subFeatures[0]!.get('name');
                } else {
                    tooltipText = `${subFeatures[0]!.get('name')} (1 / ${subFeatures.length})`;
                }
            } else {
                this.setState({ isToolTipVisible: false });
                return;
            }

            let { clientX: left, clientY: top } = evt.originalEvent as MouseEvent;
            if (this.mapDiv !== null) {
                left = left - this.mapDiv.getBoundingClientRect().left;
                top = top - this.mapDiv.getBoundingClientRect().top;
            }
            this.setState({ tooltipPosition: { left, top }, tooltipText, isToolTipVisible: true });
        };

        this.map.on('pointermove', displayFeatureInfo);
        if (this.props.isDrawable) {
            this.initDraw();
        }
        if (this.props.shapeGeoJson) {
            this.map.getView().fit(this.drawSource.getExtent());
        } else {
            this.map.getView().setCenter(proj.transform([this.props.center.lng, this.props.center.lat], 'EPSG:4326', 'EPSG:3857'));
            this.map.getView().setZoom(this.props.zoom);
        }
        this.map.setTarget(this.mapDiv);
        this.addMarkers();
        this.map.on('moveend', () => {
            if (this.props.shouldClusterMarkers && this.clusterVectorLayer) {
                const newZoom = this.map.getView().getZoom() ?? 0;
                if (newZoom >= zoomLevelToStopClustering) {
                    this.clusterVectorLayer.setVisible(false);
                    this.vectorLayer.setVisible(true);
                } else {
                    this.clusterVectorLayer.setVisible(true);
                    this.vectorLayer.setVisible(false);
                }
            }
            if (this.props.onZoomChanged) {
                const newZoom = this.map.getView().getZoom();
                if (newZoom !== undefined && this.state.zoom !== newZoom) {
                    this.setState({ zoom: newZoom });
                    this.props.onZoomChanged(newZoom);
                }
            }
            if (this.props.onBoundsChanged) {
                const bounds = getBounds(this.map);
                this.props.onBoundsChanged(bounds);
            }
        });
        if (this.props.mapLoaded) {
            this.props.mapLoaded();
        }
    }

    public override componentDidUpdate(prevProps: IGenericMapProps) {
        if (this.props.center.lng !== prevProps.center.lng || this.props.center.lat !== prevProps.center.lat) {
            if (this.map) {
                this.map.getView().setCenter(proj.transform([this.props.center.lng, this.props.center.lat], 'EPSG:4326', 'EPSG:3857'));
            }
        }

        if (this.props.zoom !== prevProps.zoom) {
            if (this.map) {
                this.map.getView().setZoom(this.props.zoom);
            }
        }

        if (JSON.stringify(this.props.markers ?? []) !== JSON.stringify(prevProps.markers ?? [])) {
            if (this.map) {
                this.addMarkers();
            }
        }

        if (JSON.stringify(this.props.markersToFit ?? []) !== JSON.stringify(prevProps.markersToFit ?? [])) {
            if (this.map) {
                this.fitMarkers(this.props.markersToFit);
            }
        }
    }

    public override render() {
        let mapStyle = { height: '100%' };
        if (this.props.inLine) {
            mapStyle = { height: '470px' };
        }
        return (
            <appContext.Consumer>
                {(state) => (
                    <>
                        <div className="openlayers-map" style={mapStyle} ref={(el) => (this.mapDiv = el)}>
                            <div
                                id="tooltip-anchor"
                                style={{
                                    left: this.state.tooltipPosition.left,
                                    top: this.state.tooltipPosition.top,
                                    display: 'block',
                                    position: 'absolute',
                                    zIndex: 1,
                                }}
                            ></div>

                            <Tooltip
                                anchorId="tooltip-anchor"
                                content={this.state.tooltipText}
                                isOpen={this.state.isToolTipVisible}
                                style={{ zIndex: 2 }}
                            />
                            {this.props.children}
                        </div>
                        <>
                            {this.props.isPdokMapLayerAvailable && (
                                <div className="map-menu-pdok">
                                    <div className="menu-item menu-top" onClick={() => this.togglePdok()}>
                                        {(this.state.pdokLayersVisible && <i className="fa fa-check-square" />) || <i className="fa fa-square" />}
                                        {translateKeyLang(state.languageCode, 'object.pdokLayer')}
                                    </div>
                                    {this.props.isDroneNoFlyMapNlAvailable && (
                                        <div className="menu-item menu-bottom" onClick={() => this.toggleDroneNoFly()}>
                                            {(this.state.droneNoFlyLayersVisible && <i className="fa fa-check-square" />) || (
                                                <i className="fa fa-square" />
                                            )}
                                            Drone no-fly zones (NL)
                                        </div>
                                    )}
                                </div>
                            )}
                            {this.props.isDrawable && (
                                <>
                                    <div className="map-menu-shape">
                                        <span className="mr30" onClick={() => this.props.onShowObjects?.()}>
                                            {(this.props.isShowObjects && <i className="fa fa-check-square" />) || <i className="fa fa-square" />}
                                            &nbsp;{translateKeyLang(state.languageCode, 'object.showOtherObjects')}
                                            <span className="tooltipZXY">
                                                <i className="fa fa-info fs12 text-info ml10" style={{ color: '#dfedf9' }} />
                                                <span className="tooltiptext">
                                                    {translateKeyLang(state.languageCode, 'object.showOtherObjectsExplanation')}
                                                </span>
                                            </span>
                                        </span>

                                        <span>{translateKeyLang(state.languageCode, 'object.shape')}</span>
                                        <select
                                            value={this.state.drawType}
                                            onChange={async (e) => await this.onDrawTypeChange(e.target.value as GeometryType)}
                                        >
                                            <option value={'LineString'}>{translateKeyLang(state.languageCode, 'object.line')}</option>
                                            <option value={'Polygon'}>{translateKeyLang(state.languageCode, 'object.polygon')}</option>
                                        </select>

                                        {this.props.shapeGeoJson !== undefined && (
                                            <button className="ml10 btn btn-xs btn-default" onClick={() => this.clearDrawing()}>
                                                {translateKeyLang(state.languageCode, 'object.clear')}
                                            </button>
                                        )}
                                    </div>
                                </>
                            )}
                        </>
                    </>
                )}
            </appContext.Consumer>
        );
    }

    private async onDrawTypeChange(drawType: GeometryType) {
        await this.setStatePromised({ drawType });
        this.initDraw();
    }

    private initDraw() {
        if (this.draw) {
            this.map.removeInteraction(this.draw);
        }
        this.draw = new Draw({
            source: this.drawSource,
            type: this.state.drawType,
        });
        this.map.addInteraction(this.draw);
        this.draw.on('drawstart', () => {
            this.clearDrawing();
        });
        this.draw.on('drawend', (e) => {
            const writer = new GeoJSON();
            const geojsonStr = writer.writeFeatures([e.feature]);
            this.props.onShapeGeoJsonChange?.(JSON.parse(geojsonStr));
        });
    }

    private clearDrawing() {
        this.props.onShapeGeoJsonChange?.(undefined);
        this.drawSource?.clear();
    }

    private togglePdok() {
        if (this.state.pdokLayersVisible) {
            this.generalMapLayer.setVisible(true);
            this.pdokMapLayer.setVisible(false);
        } else {
            this.generalMapLayer.setVisible(false);
            this.pdokMapLayer.setVisible(true);
        }
        this.pdokSatelliteLayer.setVisible(false);
        this.generalSatelliteLayer.setVisible(false);
        this.pdokInfraRedLayer.setVisible(false);
        this.setState({ pdokLayersVisible: !this.state.pdokLayersVisible });
    }

    private toggleDroneNoFly() {
        this.wmsDroneNoFlyLayer.setVisible(!this.state.droneNoFlyLayersVisible);
        this.setState({ droneNoFlyLayersVisible: !this.state.droneNoFlyLayersVisible });
    }

    private addMarkers() {
        if (this.vectorLayer) {
            this.map.removeLayer(this.vectorLayer);
        }
        if (this.clusterVectorLayer) {
            this.map.removeLayer(this.clusterVectorLayer);
        }

        // TODO: fix type
        const styleFunction: any = (feature: ExtendedFeature) => {
            if (feature.values_?.conditionLabel) {
                const { conditionLabel } = feature.values_;
                const style = new Style({
                    image: new RegularShape({
                        radius: 16,
                        stroke: new Stroke({
                            color: '#fff',
                        }),
                        angle: Math.PI / 4,
                        points: 4,
                        fill: new Fill({
                            color: conditionLabel.backgroundColor,
                        }),
                    }),
                    text: new Text({
                        text: feature.values_.conditionLabel.text,
                        fill: new Fill({
                            color: '#fff',
                        }),
                        font: '12px sans-serif',
                    }),
                });
                return style;
            }

            let markerColor = feature.values_?.marker?.color ?? 'grey';
            const isTerrain = feature.values_?.marker?.isTerrain ?? false;
            const condition = feature.values_?.marker?.condition;
            if (isTerrain && !condition) {
                markerColor = '#333';
            }

            const image = new Circle({
                radius: 5,
                fill: new Fill({
                    color: markerColor,
                }),
                stroke: new Stroke({ color: markerColor, width: 1 }),
            });

            const styles = {
                Point: new Style({
                    image,
                }),

                LineString: new Style({
                    stroke: new Stroke({
                        color: markerColor,
                        width: 4,
                    }),
                }),
                MultiLineString: new Style({
                    stroke: new Stroke({
                        color: markerColor,
                        width: 3,
                    }),
                }),
                MultiPoint: new Style({
                    image,
                }),
                MultiPolygon: new Style({
                    stroke: new Stroke({
                        color: markerColor,
                        lineDash: isTerrain ? [1, 4] : [1],
                        width: 2,
                    }),
                    fill: new Fill({
                        color: isTerrain ? 'rgba(255, 255, 255, 0.2)' : this.hexToTransparentRgbA(markerColor),
                    }),
                }),
                Polygon: new Style({
                    stroke: new Stroke({
                        color: markerColor,
                        lineDash: isTerrain ? [1, 4] : [1],
                        width: 3,
                    }),
                    fill: new Fill({
                        color: isTerrain ? 'rgba(255, 255, 255, 0.2)' : this.hexToTransparentRgbA(markerColor),
                    }),
                }),
                GeometryCollection: new Style({
                    stroke: new Stroke({
                        color: 'grey',
                        width: 2,
                    }),
                    fill: new Fill({
                        color: markerColor,
                    }),
                    image: new Circle({
                        radius: 10,
                        fill: undefined,
                        stroke: new Stroke({
                            color: markerColor,
                        }),
                    }),
                }),
                Circle: new Style({
                    stroke: new Stroke({
                        color: markerColor,
                        width: 1,
                    }),
                    fill: new Fill({
                        color: markerColor,
                    }),
                }),
            } as any;

            return styles[feature.getGeometry().getType()];
        };

        const vectorSource = new VectorSource({});

        if (this.props.markers) {
            for (const marker of this.props.markers) {
                const iconFeature = new Feature({
                    geometry: marker.shapeGeoJson
                        ? (new GeoJSON() as any).readGeometryFromObject(marker.shapeGeoJson)
                        : new Point(proj.transform([marker.position.lng, marker.position.lat], 'EPSG:4326', 'EPSG:3857')),
                    name: marker.title,
                    marker,
                });
                vectorSource.addFeature(iconFeature);

                if (marker.conditionLabel && marker.conditionLabel.text) {
                    const coordinate = marker.shapeGeoJson
                        ? (new GeoJSON() as any).readGeometryFromObject(marker.shapeGeoJson).getExtent()
                        : proj.transform([marker.position.lng, marker.position.lat], 'EPSG:4326', 'EPSG:3857');

                    const conditionLabelFeature = new Feature({
                        geometry: new Point(coordinate),
                        name: marker.title,
                        marker,
                        conditionLabel: marker.conditionLabel,
                    });
                    vectorSource.addFeature(conditionLabelFeature);
                }
            }
        }

        const clusterFeatures: Feature<any>[] = [];

        if (this.props.markers) {
            for (const marker of this.props.markers) {
                const iconFeature = new Feature({
                    geometry: new Point(proj.transform([marker.position.lng, marker.position.lat], 'EPSG:4326', 'EPSG:3857')),
                    name: marker.title,
                    marker,
                });
                iconFeature.set('pointForCluster', new Point(proj.transform([marker.position.lng, marker.position.lat], 'EPSG:4326', 'EPSG:3857')));
                clusterFeatures.push(iconFeature);
            }
        }

        const clusterVectorSource = new VectorSource({
            features: clusterFeatures,
        });

        this.vectorLayer = new VectorLayer({
            source: vectorSource,
            style: styleFunction,
        });
        this.map.addLayer(this.vectorLayer);

        if (this.props.shouldClusterMarkers) {
            const clusterSource = new Cluster({
                distance: 30,
                source: clusterVectorSource,
                geometryFunction: (feature) => feature.get('pointForCluster'),
            });

            const styleCache: Record<number, Style> = {};
            this.clusterVectorLayer = new VectorLayer({
                source: clusterSource,
                style: (feature) => {
                    if (!this.clusterVectorLayer?.getVisible()) {
                        return null;
                    }
                    const size: number = feature.get('features').length;
                    if (size > 1) {
                        let style = styleCache[size];
                        if (!style) {
                            style = new Style({
                                image: new CircleStyle({
                                    radius: 10,
                                    stroke: new Stroke({
                                        color: '#fff',
                                    }),
                                    fill: new Fill({
                                        color: '#3399CC',
                                    }),
                                }),
                                text: new Text({
                                    text: size.toString(),
                                    fill: new Fill({
                                        color: '#fff',
                                    }),
                                }),
                            });
                            styleCache[size] = style;
                        }
                        return style;
                    }
                    const originalFeature = feature.get('features')[0] ?? feature;
                    const style = styleFunction(originalFeature);
                    style.setGeometry(originalFeature.getGeometry());
                    return style;
                },
            });
            this.map.addLayer(this.clusterVectorLayer);

            if (this.state.zoom >= zoomLevelToStopClustering) {
                this.vectorLayer.setVisible(true);
                this.clusterVectorLayer.setVisible(false);
            } else {
                this.vectorLayer.setVisible(false);
                this.clusterVectorLayer.setVisible(true);
            }
        }
    }

    private async getPdokTileSource(layerName: string, layerType: 'rgb' | 'infraRed' | 'map') {
        const wmtsFormat = new WMTSCapabilities();

        let capUrl = 'https://service.pdok.nl/hwh/luchtfotorgb/wmts/v1_0?&request=GetCapabilities&service=wmts';
        if (layerType === 'infraRed') {
            capUrl = 'https://service.pdok.nl/hwh/luchtfotocir/wmts/v1_0?&request=GetCapabilities&service=wmts';
        } else if (layerType === 'map') {
            capUrl = 'https://geodata.nationaalgeoregister.nl/tiles/service/wmts?request=GetCapabilities&service=WMTS';
        }
        try {
            const response = await fetch(capUrl);
            const text = await response.text();
            const result = wmtsFormat.read(text);
            const options = optionsFromCapabilities(result, {
                layer: layerName,
                matrixSet: 'EPSG:3857',
            });
            if (!options) {
                console.log('No options available');
                return undefined;
            }
            return new WMTS(options);
        } catch (_e) {
            console.log('No map data available');
            return undefined;
        }
    }

    private hexToTransparentRgbA(rgbHex?: string) {
        let c: any;
        if (rgbHex && /^#([A-Fa-f0-9]{3}){1,2}$/.test(rgbHex)) {
            c = rgbHex.substring(1).split('');
            if (c.length === 3) {
                c = [c[0], c[0], c[1], c[1], c[2], c[2]];
            }
            c = `0x${c.join('')}`;
            // eslint-disable-next-line no-bitwise
            return `rgba(${[(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',')},0.25)`;
        }
        return rgbHex;
    }

    // Based on https://openlayers.org/en/latest/examples/measure.html
    private startMeasurement(measurementType: 'length' | 'area') {
        const geometryType = measurementType === 'length' ? 'LineString' : 'Polygon';

        this.setState({ isMeasurementActive: true });

        if (!this.measurementSource) {
            this.measurementSource = new VectorSource();
            const measurementLayer = new VectorLayer({
                source: this.measurementSource,
                style: new Style({
                    fill: new Fill({
                        color: 'rgba(255, 255, 255, 0.2)',
                    }),
                    stroke: new Stroke({
                        color: '#ffcc33',
                        width: 2,
                    }),
                    image: new CircleStyle({
                        radius: 7,
                        fill: new Fill({
                            color: '#ffcc33',
                        }),
                    }),
                }),
            });
            this.map.addLayer(measurementLayer);
        }

        if (this.measurementDraw) {
            this.map.removeInteraction(this.measurementDraw);
        }

        this.measurementDraw = new Draw({
            source: this.measurementSource,
            type: geometryType,
            style: new Style({
                fill: new Fill({
                    color: 'rgba(255, 255, 255, 0.2)',
                }),
                stroke: new Stroke({
                    color: 'rgba(0, 0, 0, 0.5)',
                    lineDash: [10, 10],
                    width: 2,
                }),
                image: new CircleStyle({
                    radius: 5,
                    stroke: new Stroke({
                        color: 'rgba(0, 0, 0, 0.7)',
                    }),
                    fill: new Fill({
                        color: 'rgba(255, 255, 255, 0.2)',
                    }),
                }),
            }),
        });

        this.map.addInteraction(this.measurementDraw);

        let measureTooltip: Overlay | undefined = undefined;
        let measureTooltipElement = document.createElement('div');

        const createMeasureTooltip = () => {
            if (measureTooltipElement) {
                measureTooltipElement.parentNode?.removeChild(measureTooltipElement);
            }
            measureTooltipElement = document.createElement('div');
            this.measurementElements.push(measureTooltipElement);
            measureTooltipElement.className = 'ol-tooltip ol-tooltip-measure';
            measureTooltip = new Overlay({
                element: measureTooltipElement,
                offset: [0, -15],
                positioning: 'bottom-center',
            });
            this.map.addOverlay(measureTooltip);
        };

        createMeasureTooltip();

        const helpTooltipElement = document.createElement('div');
        this.measurementElements.push(helpTooltipElement);
        helpTooltipElement.className = 'ol-tooltip hidden';
        const helpTooltip = new Overlay({
            element: helpTooltipElement,
            offset: [15, 0],
            positioning: 'center-left',
        });
        this.map.addOverlay(helpTooltip);

        let listener: EventsKey | undefined;
        let sketch: Feature<Geometry> | undefined;
        this.measurementDraw.on('drawstart', function (drawEvent) {
            sketch = drawEvent.feature;
            if (!sketch) {
                return;
            }

            listener = sketch.getGeometry()!.on('change', function (geometryEvent) {
                let tooltipCoord: Coordinate;
                const geom = geometryEvent.target as Geometry;
                let output;
                if (geom instanceof Polygon) {
                    output = formatArea(geom);
                    tooltipCoord = geom.getInteriorPoint().getCoordinates();
                } else if (geom instanceof LineString) {
                    output = formatLength(geom);
                    tooltipCoord = geom.getLastCoordinate();
                } else {
                    throw Error(`Unexpected geometry "${geom.getType()}"`);
                }
                measureTooltipElement.innerHTML = output;
                if (measureTooltip) {
                    measureTooltip.setPosition(tooltipCoord);
                }
            });
        });

        this.measurementDraw.on('drawend', function () {
            measureTooltipElement.className = 'ol-tooltip ol-tooltip-static';
            if (measureTooltip) {
                measureTooltip.setOffset([0, -7]);
                // unset sketch
                sketch = undefined;
                createMeasureTooltip();
                if (listener) {
                    unByKey(listener);
                }
            }
        });
    }

    private removeMeasurements() {
        for (const element of this.measurementElements) {
            element.parentNode?.removeChild(element);
        }
        this.measurementSource?.clear();
    }

    private stopMeasurements() {
        if (this.measurementDraw) {
            this.map.removeInteraction(this.measurementDraw);
        }
        this.setState({ isMeasurementActive: false });
    }

    private fitMarkers(markersToFit: IMarker[] | undefined) {
        if ((markersToFit ?? []).length === 0) {
            return;
        }

        const features: Feature<any>[] = [];
        for (const marker of markersToFit!) {
            const feature = new Feature({
                geometry: marker.shapeGeoJson
                    ? (new GeoJSON() as any).readGeometryFromObject(marker.shapeGeoJson)
                    : new Point(proj.transform([marker.position.lng, marker.position.lat], 'EPSG:4326', 'EPSG:3857')),
                name: marker.title,
                marker,
            });
            features.push(feature);
        }

        const clusterExtend = createEmpty();

        for (const featureInCluster of features) {
            const featureGeometry = featureInCluster.getGeometry();
            if (featureGeometry) {
                extend(clusterExtend, featureGeometry.getExtent());
            }
        }

        this.map.getView().fit([clusterExtend[0]! - 100, clusterExtend[1]! - 100, clusterExtend[2]! + 100, clusterExtend[3]! + 100]);
    }
}
